import React from 'react';
import { ActionIcon, AppShell, Box, Divider, ScrollArea, Stack, Text, Tooltip, rem } from '@mantine/core';
import { IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigationResolve } from '@/function/navigation/navigation.find';
import { useAppDispatch, useAppSelector } from '@/store/store.hooks';
import { selectOpenBranches } from '@/store/ui/ui.selectors';
import { focusBranches, toggleBranch } from '@/store/ui/ui.slice';
import type { TypeNavNode } from '@/types/ui/types.ui.navigation';
import { NAVIGATION_TREE } from './config/navigation.tree';
import ShellNavbarCollapsedNode from './Shell.Navbar.Collapsed.Node';
import ShellNavbarNode from './Shell.Navbar.Node';
import { useChatUnreadBadge } from '@/hooks/use.chat.unread.badge';
import ShellNavbarProfile from './Shell.Navbar.Profile';

const GROUP_STARTS: Record<string, string> = {
    traffic: 'Piattaforma',
    settings: '',
};

type Props = {
    expanded: boolean;
    onToggle: () => void;
    onNavigateMobile?: () => void;
    showToggle?: boolean;
};

const ShellNavbar: React.FC<Props> = ({ expanded, onToggle, onNavigateMobile, showToggle = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const openBranches = useAppSelector(selectOpenBranches);
    const badges = useChatUnreadBadge();

    React.useEffect(() => {
        const resolved = navigationResolve(NAVIGATION_TREE, location.pathname);
        if (!resolved) return;
        const ids = resolved.trail.filter((node) => node.children?.length).map((node) => node.id);
        const same = ids.length === openBranches.length && ids.every((id) => openBranches.includes(id));
        if (!same) dispatch(focusBranches(ids));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleNavigate = (node: TypeNavNode) => {
        navigate(node.path);
        onNavigateMobile?.();
    };

    const toggleLabel = expanded ? 'Riduci la barra laterale' : 'Espandi la barra laterale';

    return (
        <>
            {showToggle ? (
                <AppShell.Section px="xs" pt="xs" pb={4}>
                    <Box
                        style={{
                            display: 'flex', alignItems: 'center',
                            justifyContent: expanded ? 'flex-end' : 'center',
                            height: rem(32),
                        }}
                    >
                        <Tooltip label={toggleLabel} position="right">
                            <ActionIcon variant="subtle" color="gray" size="md" onClick={onToggle} aria-label={toggleLabel} aria-expanded={expanded}>
                                {expanded
                                    ? <IconLayoutSidebarLeftCollapse size={18} stroke={1.7} />
                                    : <IconLayoutSidebarLeftExpand size={18} stroke={1.7} />}
                            </ActionIcon>
                        </Tooltip>
                    </Box>
                </AppShell.Section>
            ) : null}

            <AppShell.Section grow component={ScrollArea} scrollbarSize={6} type="scroll" py={4}>
                <Stack gap={2} px="xs">
                    {NAVIGATION_TREE.map((node) => (
                        <React.Fragment key={node.id}>
                            {node.id in GROUP_STARTS ? (
                                expanded && GROUP_STARTS[node.id] ? (
                                    <Text size="xs" c="dimmed" fw={500} px={12} pt="md" pb={4}>{GROUP_STARTS[node.id]}</Text>
                                ) : <Divider my="xs" mx={expanded ? 12 : 4} />
                            ) : null}
                            {expanded ? (
                                <ShellNavbarNode
                                    node={node}
                                    depth={0}
                                    pathname={location.pathname}
                                    openBranches={openBranches}
                                    onToggleBranch={(id) => dispatch(toggleBranch(id))}
                                    onNavigate={handleNavigate}
                                    badges={badges}
                                />
                            ) : (
                                <ShellNavbarCollapsedNode node={node} pathname={location.pathname} onNavigate={handleNavigate} badge={badges[node.id] ?? 0} />
                            )}
                        </React.Fragment>
                    ))}
                </Stack>
            </AppShell.Section>

            <AppShell.Section>
                <Divider />
                <Box px="xs" py="xs" style={{ paddingBottom: 'calc(var(--mantine-spacing-xs) + env(safe-area-inset-bottom, 0px))' }}>
                    <ShellNavbarProfile expanded={expanded} onNavigate={onNavigateMobile} />
                </Box>
            </AppShell.Section>
        </>
    );
};

export default ShellNavbar;
